import React from 'react';
import { Header } from './components/Header';
import { Col, Row, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { ProfileForm } from './components/ProfileForm';
import { VerificationForm } from './components/VerificationForm';
import { OrganizationForm } from './components/OrganizationForm';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Pagination } from '../../components/common/Pagination';
import { Loader } from '../../components/common/Loader';
import { RootStates } from '../../redux/store';
import {
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { onLogout } from 'redux/authSlice';
import { useTranslation } from 'react-i18next';
import { routesPaths } from 'constants/paths';
import './Onboarding.scss';

export const Onboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootStates) => state.auth.userDetails);
  const { page = '0' } = useParams();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [open, setOpen] = React.useState(false);

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleLogout = () => dispatch(onLogout());

  const renderForm = (children: React.ReactNode) => {
    switch (page) {
      case '1': {
        if (
          user.onboardingData?.isEmailVerified &&
          user.onboardingData?.isMobileVerified
        ) {
          return <Navigate to="/onboarding/2" />;
        }

        return <VerificationForm>{children}</VerificationForm>;
      }
      case '2':
        return <OrganizationForm>{children}</OrganizationForm>;
      default:
        return <ProfileForm>{children}</ProfileForm>;
    }
  };

  const handlePaginationClick = React.useCallback(
    (page: number) => {
      if ((user.onboardingData?.lastPageFilled ?? 0) >= page) {
        navigate(`/onboarding/${page}`);
      } else {
        toast.error(t('error_fill_onboarding_form'));
      }
    },
    [navigate, t, user.onboardingData?.lastPageFilled],
  );

  const PaginationComponent = React.useMemo(
    () => (
      <Pagination
        pages={3}
        activePage={Number(page)}
        onClick={handlePaginationClick}
      />
    ),
    [handlePaginationClick, page],
  );

  return user.onboardingData?.isCompleted ? (
    <Navigate to={routesPaths.projects} />
  ) : (
    <Container fluid className="onboarding d-flex">
      <Row className="row-no-gutters col-12">
        <Col md={8} className="welcome-wrapper d-flex">
          <div className={`welcome-bg bg-${page}`} />
          {PaginationComponent}
        </Col>
        <Col md={4} className="onboard-form d-flex">
          <Header />
          <IconButton
            className="more-btn position-fixed br-50"
            onClick={handleMoreClick}
          >
            <MoreVert />
          </IconButton>
          {user._id || user.name || user.email || user.mobile ? (
            renderForm(PaginationComponent)
          ) : (
            <Loader />
          )}
        </Col>
      </Row>
      <Popover
        id="work-flow-popper"
        className="work-flow-popper"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List className="workflow-list">
          <ListItemButton onClick={handleLogout}>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </List>
      </Popover>
    </Container>
  );
};
